import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Grid2, Paper, Stack, Typography } from '@mui/material';

import { usePageTitle } from '@enroller/utils';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { FieldOption, FormField, FormFieldType } from '@enroller/admissions-components';
import {
  defaultValues,
  MultipleVaccineDosesFormNames,
  SingleVaccineDosesFormNames,
  VaccinationDose,
  VaccinationsFormValues,
  vaccinationsSchema,
} from './VaccinationsFormValues.ts';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext.tsx';

const countPostfixes: Record<number, string> = {
  1: 'st',
  2: 'nd',
  3: 'rd',
};

// Radio options for vaccination doses type mapped per vaccine form name
const vaccinationTypeOptions: Record<string, FieldOption[]> = {
  [MultipleVaccineDosesFormNames.diphtheria]: [
    { label: 'DPT', value: 'DPT' },
    { label: 'DTaP', value: 'DTaP' },
    { label: 'Tdap', value: 'Tdap' },
  ],
  [SingleVaccineDosesFormNames.COVID19]: [
    { label: 'AstraZeneca', value: 'AstraZeneca' },
    { label: 'Janssen (J&J)', value: 'Janssen (J&J)' },
    { label: 'Moderna', value: 'Moderna' },
    { label: 'Pfizer-BioNTech', value: 'Pfizer-BioNTech' },
  ],
};

export const VaccinationsForm = () => {
  return (
    <PageContainer>
      <VaccinationRecordForm />
      <VaccinationDosesSection vaccineName="Diphtheria / Tetanus / Pertussis" vaccineFormName={MultipleVaccineDosesFormNames.diphtheria} />
      <VaccinationDosesSection vaccineName="Polio" vaccineFormName={MultipleVaccineDosesFormNames.polio} />
      <VaccinationDosesSection vaccineName="Hepatitis A" vaccineFormName={MultipleVaccineDosesFormNames.hepatitisA} />
      <VaccinationDosesSection vaccineName="Hepatitis B" vaccineFormName={MultipleVaccineDosesFormNames.hepatitisB} />
      <VaccinationDosesSection vaccineName="Measles" vaccineFormName={MultipleVaccineDosesFormNames.measles} />
      <VaccinationDosesSection vaccineName="Mumps" vaccineFormName={MultipleVaccineDosesFormNames.mumps} />
      <VaccinationDosesSection vaccineName="Rubella" vaccineFormName={MultipleVaccineDosesFormNames.rubella} />
      <VaccinationDosesSection vaccineName="Meningitis" vaccineFormName={MultipleVaccineDosesFormNames.meningitis} />
      <CovidVaccineSection />
      <TuberculosisTestSection />
    </PageContainer>
  );
};

const VaccinationRecordForm = () => {
  const { control } = useFormContext<VaccinationsFormValues>();

  return (
    <PaperSection pageTitle="Vaccinations" sectionTitle="Vaccination Record Form">
      <Grid2 size={12}>
        <FormField label="" control={control} name="vaccinationRecordFormDocument" id="vaccinationRecordFormDocumentId" type={FormFieldType.PictureUploader} />
      </Grid2>
    </PaperSection>
  );
};

interface VaccinationDosesSectionProps {
  vaccineName: string;
  vaccineFormName: MultipleVaccineDosesFormNames;
}

const VaccinationDosesSection = ({ vaccineName, vaccineFormName }: VaccinationDosesSectionProps) => {
  const { control } = useFormContext<VaccinationsFormValues>();
  const { fields } = useFieldArray({
    control,
    name: vaccineFormName,
  });

  return (
    <PaperSection sectionTitle={`${vaccineName} (${fields.length} doses)`}>
      {fields.map((field, i) => (
        <VaccinationDoseForm key={field.id} vaccineName={vaccineName} vaccineFormName={vaccineFormName} field={field} index={i} />
      ))}
    </PaperSection>
  );
};

interface VaccinationDoseFormProps extends VaccinationDosesSectionProps {
  index: number;
  field: VaccinationDose;
}

const VaccinationDoseForm = ({ field, vaccineName, index, vaccineFormName }: VaccinationDoseFormProps) => {
  const { control, watch } = useFormContext<VaccinationsFormValues>();

  const recordSelected = watch(`${vaccineFormName}.${index}.recordDocument.value`);

  // Check the default value from API or watch for changes
  const isDocumentRequired = field.recordDocument.value === 'other' || recordSelected === 'other';
  const countPostfix = countPostfixes[index + 1] ?? 'th';

  return (
    <PaperSection nested sectionTitle={`${vaccineName} ${index + 1}${countPostfix} dose`}>
      <Grid2 size={3}>
        <FormField
          max={0.1} // today
          maxPeriod="days"
          maxDirection="after"
          label="Date"
          control={control}
          type={FormFieldType.Date}
          name={`${vaccineFormName}.${index}.date`}
        />
      </Grid2>

      <Grid2 size={12}>
        <FormField
          label="Indicate if this is included in the Greenheart Vaccination Record document or Other if you are uploading separate documentation."
          control={control}
          type={FormFieldType.Radio}
          options={[
            { label: 'Vaccination record', value: 'vaccination record' },
            { label: 'Other', value: 'other' },
          ]}
          name={`${vaccineFormName}.${index}.recordDocument.value`}
        />
      </Grid2>

      {isDocumentRequired && (
        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            name={`${vaccineFormName}.${index}.recordDocument.document`}
            id={`${vaccineFormName}.${index}.documentId`} // Pick up the default value from API or create a new one
            type={FormFieldType.PictureUploader}
          />
        </Grid2>
      )}
    </PaperSection>
  );
};

const CovidVaccineSection = () => {
  const { control, getValues, watch } = useFormContext<VaccinationsFormValues>();

  // Check the default value from API or watch for changes
  const field = getValues(SingleVaccineDosesFormNames.COVID19);
  const recordDocumentWatch = watch(`${SingleVaccineDosesFormNames.COVID19}.recordDocument.value`);

  const isDocumentRequired = field.recordDocument.value === 'other' || recordDocumentWatch === 'other';

  return (
    <PaperSection sectionTitle="COVID-19">
      <PaperSection nested sectionTitle="COVID Booster">
        <Grid2 size={3}>
          <FormField
            max={0.1} // today
            maxPeriod="days"
            maxDirection="after"
            label="Date"
            control={control}
            type={FormFieldType.Date}
            name={`${SingleVaccineDosesFormNames.COVID19}.date`}
          />
        </Grid2>

        <Grid2 size="auto">
          <FormField
            label="Type"
            control={control}
            type={FormFieldType.Radio}
            options={vaccinationTypeOptions[SingleVaccineDosesFormNames.COVID19]}
            name={`${SingleVaccineDosesFormNames.COVID19}.vaccineType`}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label="Indicate if this is included in the Greenheart Vaccination Record document or Other if you are uploading separate documentation."
            control={control}
            type={FormFieldType.Radio}
            options={[
              { label: 'Vaccination record', value: 'vaccination record' },
              { label: 'Other', value: 'other' },
            ]}
            name={`${SingleVaccineDosesFormNames.COVID19}.recordDocument.value`}
          />
        </Grid2>

        {isDocumentRequired && (
          <Grid2 size={12}>
            <FormField
              label=""
              control={control}
              name={`${SingleVaccineDosesFormNames.COVID19}.recordDocument.document`}
              id={field.recordDocument.document.id ?? `${SingleVaccineDosesFormNames.COVID19}.documentId`} // Pick up the default value from API or create a new one
              type={FormFieldType.PictureUploader}
            />
          </Grid2>
        )}
      </PaperSection>
    </PaperSection>
  );
};

const TuberculosisTestSection = () => {
  const { control, getValues, watch } = useFormContext<VaccinationsFormValues>();

  // Check the default value from API or watch for changes
  const field = getValues(SingleVaccineDosesFormNames.tuberculosisTest);
  const recordDocumentWatch = watch(`${SingleVaccineDosesFormNames.tuberculosisTest}.testStatus`);

  const isDocumentRequired = field.testStatus.result === 'positive' || recordDocumentWatch === 'positive';

  return (
    <PaperSection sectionTitle="Tuberculosis Test">
      <PaperSection nested sectionTitle="Tuberculosis Test">
        <Grid2 size={3}>
          <FormField
            max={0.1} // today
            maxPeriod="days"
            maxDirection="after"
            label="Date"
            control={control}
            type={FormFieldType.Date}
            name={`${SingleVaccineDosesFormNames.tuberculosisTest}.date`}
          />
        </Grid2>

        <Grid2 size="auto">
          <FormField
            label="Tuberculosis Test Result"
            control={control}
            type={FormFieldType.Radio}
            options={[
              { label: 'Negative', value: 'negative' },
              { label: 'Positive', value: 'positive' },
            ]}
            name={`${SingleVaccineDosesFormNames.tuberculosisTest}.testStatus.result`}
          />
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            If the applicant tested positive for the Tuberculosis Skin Test; a report of a negative chest x-ray is required
          </Alert>
        </Grid2>

        {isDocumentRequired && (
          <Grid2 size={12}>
            <FormField
              label=""
              control={control}
              name={`${SingleVaccineDosesFormNames.tuberculosisTest}.testStatus.document`}
              id={field.testStatus.document?.id ?? `${SingleVaccineDosesFormNames.tuberculosisTest}.documentId`}
              type={FormFieldType.PictureUploader}
            />
          </Grid2>
        )}
      </PaperSection>
    </PaperSection>
  );
};

const PageContainer = ({ children }: { children: ReactNode }) => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Vaccination Record', subtitle: 'Vaccination Record Form' });
    setPageTitle('Vaccination Record');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? vaccinationsSchema.required : vaccinationsSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const formMethods = useForm<VaccinationsFormValues>({
    defaultValues,
    resolver,
  });

  const { handleSubmit, trigger } = formMethods;

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('vaccinations', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          setIsFormLoading(true);
          setTimeout(() => {
            console.log('Form submitted:', data);
            setIsFormLoading(false);
          }, 1000);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setIsFormLoading, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </Stack>
  );
};

const PaperSection = ({ pageTitle, sectionTitle, nested, children }: { pageTitle?: string; sectionTitle: string; nested?: boolean; children: ReactNode }) => {
  return (
    <Paper sx={{ p: 2, width: '100%', boxShadow: nested ? 'none' : undefined }}>
      <Grid2 container spacing={3}>
        {pageTitle && (
          <Grid2 size={12}>
            <Typography variant="subtitle1">{pageTitle}</Typography>
          </Grid2>
        )}

        <Grid2 size={12}>
          <Typography variant="subtitle2">{sectionTitle}</Typography>
        </Grid2>

        {children}
      </Grid2>
    </Paper>
  );
};
